import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Save, Plus, Trash2, MapPin, UserCheck, Check, Wrench } from "lucide-react";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { getRoomName, getDoctorName, getEquipmentName } from "@/utils/dataLookups";

export default function ServiceForm({
  service,
  onSave,
  onClose,
  isLoading = false,
}) {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      service_name: "",
      service_code: "",
      category: "",
      subcategory: "",
      description: "",
      duration_minutes: 60,
      preparation_time: 0,
      equipment_required: [],
      supplies_required: [],
      contraindications: [],
      side_effects: [],
      pre_service_requirements: [],
      post_service_care: [],
      frequency_recommendations: "",
      requires_doctor: false,
      requires_room: true, // Default to true for backward compatibility
      requires_lab_work: false,
      consent_form_required: false,
      status: "active",
      notes: "",
      rooms_required: [],
      doctors_assigned: [], // New field for doctors
      equipment_assigned: [], // Equipment IDs for serviceEquipment join table
      base_price: 0, // New field for service cost
    },
  });

  const {
    fields: equipmentFields,
    append: appendEquipment,
    remove: removeEquipment,
  } = useFieldArray({ control, name: "equipment_required" });

  const {
    fields: suppliesFields,
    append: appendSupply,
    remove: removeSupply,
  } = useFieldArray({ control, name: "supplies_required" });

  const {
    fields: contraindicationFields,
    append: appendContraindication,
    remove: removeContraindication,
  } = useFieldArray({ control, name: "contraindications" });

  const {
    fields: sideEffectFields,
    append: appendSideEffect,
    remove: removeSideEffect,
  } = useFieldArray({ control, name: "side_effects" });

  const {
    fields: preReqFields,
    append: appendPreReq,
    remove: removePreReq,
  } = useFieldArray({ control, name: "pre_service_requirements" });

  const {
    fields: postCareFields,
    append: appendPostCare,
    remove: removePostCare,
  } = useFieldArray({ control, name: "post_service_care" });

  const watchRequiresDoctor = watch("requires_doctor");
  const watchRequiresRoom = watch("requires_room");
  const watchRoomsRequired = watch("rooms_required");
  const watchDoctorsAssigned = watch("doctors_assigned");
  const watchEquipmentAssigned = watch("equipment_assigned");

  // Fetch rooms and doctors data
  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ["rooms"],
    queryFn: () => apiClient.get(ENDPOINTS.SETTINGS.ROOMS),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: doctors = [], isLoading: doctorsLoading } = useQuery({
    queryKey: ["doctors"],
    queryFn: () => apiClient.get("/api/doctors"),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: allEquipment = [], isLoading: equipmentLoading } = useQuery({
    queryKey: ["equipment"],
    queryFn: () => apiClient.get(ENDPOINTS.SETTINGS.EQUIPMENT),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (service) {
      reset({
        ...service,
        equipment_required: (service.equipment_required || []).map((item) =>
          typeof item === "string" ? { value: item } : item
        ),
        supplies_required: service.supplies_required || [],
        contraindications: (service.contraindications || []).map((item) =>
          typeof item === "string" ? { value: item } : item
        ),
        side_effects: (service.side_effects || []).map((item) =>
          typeof item === "string" ? { value: item } : item
        ),
        pre_service_requirements: (service.pre_service_requirements || []).map(
          (item) => (typeof item === "string" ? { value: item } : item)
        ),
        post_service_care: (service.post_service_care || []).map((item) =>
          typeof item === "string" ? { value: item } : item
        ),
        rooms_required: service.rooms_required || [],
        doctors_assigned: service.doctors_assigned || [],
        equipment_assigned: service.equipment_assigned || [],
        base_price: service.base_price || service.basePrice || 0,
      });
    }
  }, [service, reset]);

  // Helper functions for managing arrays
  const addRoom = (roomId) => {
    if (roomId && !watchRoomsRequired.includes(roomId)) {
      setValue("rooms_required", [...watchRoomsRequired, roomId], {
        shouldValidate: true,
      });
    }
  };

  const removeRoom = (roomId) => {
    setValue(
      "rooms_required",
      watchRoomsRequired.filter((id) => id !== roomId),
      { shouldValidate: true }
    );
  };

  const addDoctor = (doctorId) => {
    if (doctorId && !watchDoctorsAssigned.includes(doctorId)) {
      setValue("doctors_assigned", [...watchDoctorsAssigned, doctorId], {
        shouldValidate: true,
      });
    }
  };

  const removeDoctor = (doctorId) => {
    setValue(
      "doctors_assigned",
      watchDoctorsAssigned.filter((id) => id !== doctorId),
      { shouldValidate: true }
    );
  };

  const addEquipment = (equipmentId) => {
    if (equipmentId && !watchEquipmentAssigned.includes(equipmentId)) {
      setValue("equipment_assigned", [...watchEquipmentAssigned, equipmentId], {
        shouldValidate: true,
      });
    }
  };

  const removeEquipmentAssigned = (equipmentId) => {
    setValue(
      "equipment_assigned",
      watchEquipmentAssigned.filter((id) => id !== equipmentId),
      { shouldValidate: true }
    );
  };

  const onSubmit = (data) => {
    // Transform field arrays back to simple string arrays for API
    const transformedData = {
      ...data,
      equipment_required:
        data.equipment_required?.map((item) =>
          typeof item === "object" ? item.value : item
        ) || [],
      contraindications:
        data.contraindications?.map((item) =>
          typeof item === "object" ? item.value : item
        ) || [],
      side_effects:
        data.side_effects?.map((item) =>
          typeof item === "object" ? item.value : item
        ) || [],
      pre_service_requirements:
        data.pre_service_requirements?.map((item) =>
          typeof item === "object" ? item.value : item
        ) || [],
      post_service_care:
        data.post_service_care?.map((item) =>
          typeof item === "object" ? item.value : item
        ) || [],
    };
    onSave(transformedData);
  };

  const onSubmitError = (errors) => {
    const firstError = Object.keys(errors)[0];
    if (firstError) {
      setTimeout(() => {
        const errorElement =
          document.getElementById(firstError) ||
          document.querySelector(`[name="${firstError}"]`);

        if (errorElement) {
          const scrollContainer = document.querySelector(".max-h-\\[90vh\\]");
          if (scrollContainer) {
            const elementRect = errorElement.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const relativeTop =
              elementRect.top - containerRect.top + scrollContainer.scrollTop;

            scrollContainer.scrollTo({
              top:
                relativeTop - containerRect.height / 2 + elementRect.height / 2,
              behavior: "smooth",
            });
          } else {
            errorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }

          // Try to focus the first focusable element within the error section
          const focusableElement = errorElement.querySelector(
            'input, select, textarea, button:not([type="button"])'
          );
          if (focusableElement && focusableElement.focus) {
            setTimeout(() => focusableElement.focus(), 300); // Delay to allow scroll to complete
          }
        } else {
          console.warn(`Could not find element for error field: ${firstError}`);
        }
      }, 100);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white/80 backdrop-blur-sm z-10 border-b">
          <CardTitle>{service ? "Edit" : "Add New"} Service</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form
            onSubmit={handleSubmit(onSubmit, onSubmitError)}
            className="space-y-6"
          >
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service_name">Service Name *</Label>
                <Controller
                  name="service_name"
                  control={control}
                  rules={{ required: "Service name is required" }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="service_name"
                      placeholder="e.g., Plasmapheresis"
                      data-testid="service-name-input"
                    />
                  )}
                />
                {errors.service_name && (
                  <p className="text-sm text-red-600">
                    {errors.service_name.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="service_code">Service Code</Label>
                <Controller
                  name="service_code"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="service_code"
                      placeholder="CPT or internal code"
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Controller
                  name="category"
                  control={control}
                  rules={{ required: "Category is required" }}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger id="category" data-testid="service-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="iv_therapy" data-testid="service-cat-iv_therapy">IV Therapy</SelectItem>
                        <SelectItem value="blood_filtration" data-testid="service-cat-blood_filtration">
                          Blood Filtration
                        </SelectItem>
                        <SelectItem value="detox_therapy" data-testid="service-cat-detox_therapy">
                          Detox Therapy
                        </SelectItem>
                        <SelectItem value="diagnostic" data-testid="service-cat-diagnostic">Diagnostic</SelectItem>
                        <SelectItem value="consultation" data-testid="service-cat-consultation">
                          Consultation
                        </SelectItem>
                        <SelectItem value="wellness" data-testid="service-cat-wellness">Wellness</SelectItem>
                        <SelectItem value="procedure" data-testid="service-cat-procedure">Procedure</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.category && (
                  <p className="text-sm text-red-600">
                    {errors.category.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="subcategory">Subcategory</Label>
                <Controller
                  name="subcategory"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="subcategory"
                      placeholder="e.g., Therapeutic, Chelation"
                    />
                  )}
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    id="description"
                    placeholder="Detailed description of the service..."
                    rows={3}
                  />
                )}
              />
            </div>

            {/* Duration, Prep Time and Price */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration_minutes">Duration (minutes) *</Label>
                <Controller
                  name="duration_minutes"
                  control={control}
                  rules={{
                    required: "Duration is required",
                    min: {
                      value: 1,
                      message: "Duration must be at least 1 minute",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="duration_minutes"
                      type="number"
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                      data-testid="service-duration-input"
                    />
                  )}
                />
                {errors.duration_minutes && (
                  <p className="text-sm text-red-600">
                    {errors.duration_minutes.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="preparation_time">Prep Time (minutes)</Label>
                <Controller
                  name="preparation_time"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="preparation_time"
                      type="number"
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="base_price">Price (R) *</Label>
                <Controller
                  name="base_price"
                  control={control}
                  rules={{
                    required: "Price is required",
                    min: { value: 0, message: "Price cannot be negative" },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="base_price"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || 0)
                      }
                      data-testid="service-price-input"
                    />
                  )}
                />
                {errors.base_price && (
                  <p className="text-sm text-red-600">
                    {errors.base_price.message}
                  </p>
                )}
              </div>
            </div>

            {/* Room and Equipment Requirements */}
            <div className="space-y-6">
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Room & Equipment Requirements
                </h3>

                {/* Add Room Section - Only show when requires_room is true */}
                {watchRequiresRoom && (
                  <div className="space-y-4" id="rooms_required">
                    <Label>Rooms Required *</Label>
                    <Controller
                      name="rooms_required"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (watchRequiresRoom && (!value || value.length === 0)) {
                            return "At least one room must be selected";
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                      <>
                        <div className="flex gap-2">
                          <Select
                            onValueChange={(value) => {
                              if (value) {
                                if (field.value.includes(value)) {
                                  // If already selected, remove it
                                  removeRoom(value);
                                } else {
                                  // If not selected, add it
                                  addRoom(value);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger className="flex-1" data-testid="service-room-select">
                              <SelectValue placeholder="Select a room..." />
                            </SelectTrigger>
                            <SelectContent>
                              {roomsLoading ? (
                                <SelectItem value="loading" disabled>
                                  Loading rooms...
                                </SelectItem>
                              ) : rooms.length === 0 ? (
                                <SelectItem value="no-rooms" disabled>
                                  No rooms available
                                </SelectItem>
                              ) : (
                                rooms.map((room) => {
                                  const isSelected = field.value.includes(
                                    room.id
                                  );
                                  return (
                                    <SelectItem
                                      key={room.id}
                                      value={room.id}
                                      className="flex items-center justify-between"
                                      data-testid={`service-room-option-${room.name.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span>{room.name}</span>
                                        {isSelected && (
                                          <Check className="w-4 h-4 text-blue-600" />
                                        )}
                                      </div>
                                    </SelectItem>
                                  );
                                })
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Selected Rooms */}
                        <div className="space-y-2">
                          {field.value.map((roomId) => (
                            <div
                              key={roomId}
                              className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-blue-600" />
                                <span className="font-medium">
                                  {getRoomName(roomId, rooms)}
                                </span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeRoom(roomId)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  />
                  {errors.rooms_required && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.rooms_required.message}
                    </p>
                  )}
                  </div>
                )}
              </div>

              {/* Doctor Assignment Section - Only show when requires_doctor is true */}
              {watchRequiresDoctor && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <UserCheck className="w-5 h-5" />
                    Doctor Assignment
                  </h3>

                  <div className="space-y-4" id="doctors_assigned">
                    <Label>Assigned Doctors</Label>
                    <Controller
                      name="doctors_assigned"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (
                            watchRequiresDoctor &&
                            (!value || value.length === 0)
                          ) {
                            return "At least one doctor must be selected when 'Requires Doctor' is checked";
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <>
                          <div className="flex gap-2">
                            <Select
                              onValueChange={(value) => {
                                if (value) {
                                  if (field.value.includes(value)) {
                                    // If already selected, remove it
                                    removeDoctor(value);
                                  } else {
                                    // If not selected, add it
                                    addDoctor(value);
                                  }
                                }
                              }}
                              value=""
                            >
                              <SelectTrigger className="flex-1" data-testid="service-doctor-select">
                                <SelectValue placeholder="Select a doctor..." />
                              </SelectTrigger>
                              <SelectContent>
                                {doctorsLoading ? (
                                  <SelectItem value="loading" disabled>
                                    Loading doctors...
                                  </SelectItem>
                                ) : doctors.length === 0 ? (
                                  <SelectItem value="no-doctors" disabled>
                                    No doctors available
                                  </SelectItem>
                                ) : (
                                  doctors.map((doctor) => {
                                    const isSelected = field.value.includes(
                                      doctor.id
                                    );
                                    return (
                                      <SelectItem
                                        key={doctor.id}
                                        value={doctor.id}
                                        className="flex items-center justify-between"
                                        data-testid={`service-doctor-option-${doctor.name.toLowerCase().replace(/\s+/g, '-')}`}
                                      >
                                        <div className="flex items-center gap-2">
                                          <span>{doctor.name}</span>
                                          {isSelected && (
                                            <Check className="w-4 h-4 text-green-600" />
                                          )}
                                        </div>
                                      </SelectItem>
                                    );
                                  })
                                )}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Selected Doctors */}
                          <div className="space-y-2">
                            {field.value.map((doctorId) => (
                              <div
                                key={doctorId}
                                className="border border-slate-200 rounded-lg p-3 bg-green-50/50 flex items-center justify-between"
                              >
                                <div className="flex items-center gap-2">
                                  <UserCheck className="w-4 h-4 text-green-600" />
                                  <span className="font-medium">
                                    {getDoctorName(doctorId, doctors)}
                                  </span>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeDoctor(doctorId)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    />
                    {errors.doctors_assigned && (
                      <p className="text-sm text-red-600">
                        {errors.doctors_assigned.message}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Equipment Assignment Section */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Wrench className="w-5 h-5" />
                  Equipment Assignment
                </h3>

                <div className="space-y-4" id="equipment_assigned">
                  <Label>Assigned Equipment</Label>
                  <Controller
                    name="equipment_assigned"
                    control={control}
                    render={({ field }) => (
                      <>
                        <div className="flex gap-2">
                          <Select
                            onValueChange={(value) => {
                              if (value) {
                                if (field.value.includes(value)) {
                                  removeEquipmentAssigned(value);
                                } else {
                                  addEquipment(value);
                                }
                              }
                            }}
                            value=""
                          >
                            <SelectTrigger className="flex-1" data-testid="service-equipment-select">
                              <SelectValue placeholder="Select equipment..." />
                            </SelectTrigger>
                            <SelectContent>
                              {equipmentLoading ? (
                                <SelectItem value="loading" disabled>
                                  Loading equipment...
                                </SelectItem>
                              ) : allEquipment.length === 0 ? (
                                <SelectItem value="no-equipment" disabled>
                                  No equipment available
                                </SelectItem>
                              ) : (
                                allEquipment.map((eq) => {
                                  const isSelected = field.value.includes(eq.id);
                                  return (
                                    <SelectItem
                                      key={eq.id}
                                      value={eq.id}
                                      className="flex items-center justify-between"
                                      data-testid={`service-equipment-option-${eq.name.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span>{eq.name}</span>
                                        {isSelected && (
                                          <Check className="w-4 h-4 text-purple-600" />
                                        )}
                                      </div>
                                    </SelectItem>
                                  );
                                })
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Selected Equipment */}
                        <div className="space-y-2">
                          {field.value.map((equipmentId) => (
                            <div
                              key={equipmentId}
                              className="border border-slate-200 rounded-lg p-3 bg-purple-50/50 flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2">
                                <Wrench className="w-4 h-4 text-purple-600" />
                                <span className="font-medium">
                                  {getEquipmentName(equipmentId, allEquipment)}
                                </span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeEquipmentAssigned(equipmentId)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-4" id="equipment_required">
                <Label>Additional Equipment (Text)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add equipment..."
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value) {
                          appendEquipment({ value });
                          e.target.value = "";
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={(e) => {
                      const input = e.target
                        .closest("div")
                        .querySelector("input");
                      const value = input.value.trim();
                      if (value) {
                        appendEquipment({ value });
                        input.value = "";
                      }
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {equipmentFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                    >
                      {field.value}
                      <button
                        type="button"
                        onClick={() => removeEquipment(index)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Supplies Required */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label>Supplies Required</Label>
                <Button
                  type="button"
                  onClick={() =>
                    appendSupply({ item_name: "", quantity: 1, unit: "" })
                  }
                  size="sm"
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Supply
                </Button>
              </div>
              {suppliesFields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid grid-cols-4 gap-2 items-end p-3 bg-slate-50 rounded-lg"
                >
                  <Controller
                    name={`supplies_required.${index}.item_name`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Input {...inputField} placeholder="Item name" />
                    )}
                  />
                  <Controller
                    name={`supplies_required.${index}.quantity`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Input
                        {...inputField}
                        type="number"
                        placeholder="Quantity"
                        onChange={(e) =>
                          inputField.onChange(parseInt(e.target.value) || 0)
                        }
                      />
                    )}
                  />
                  <Controller
                    name={`supplies_required.${index}.unit`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Input {...inputField} placeholder="Unit" />
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSupply(index)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Requirements Checkboxes */}
            <div className="flex gap-6">
              <div className="flex items-center space-x-2">
                <Controller
                  name="requires_doctor"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="requires_doctor"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="service-requires-doctor-checkbox"
                    />
                  )}
                />
                <Label htmlFor="requires_doctor">Requires Doctor</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Controller
                  name="requires_room"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="requires_room"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="service-requires-room-checkbox"
                    />
                  )}
                />
                <Label htmlFor="requires_room">Requires Room</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Controller
                  name="requires_lab_work"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="requires_lab_work"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <Label htmlFor="requires_lab_work">Requires Lab Work</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Controller
                  name="consent_form_required"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="consent_form_required"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <Label htmlFor="consent_form_required">
                  Consent Form Required
                </Label>
              </div>
            </div>

            {/* Frequency Recommendations */}
            <div className="space-y-2">
              <Label htmlFor="frequency_recommendations">
                Frequency Recommendations
              </Label>
              <Controller
                name="frequency_recommendations"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="frequency_recommendations"
                    placeholder="e.g., Weekly for 4 weeks, then monthly"
                  />
                )}
              />
            </div>

            {/* Contraindications */}
            <div className="space-y-4" id="contraindications">
              <Label>Contraindications</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add contraindication..."
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const value = e.target.value.trim();
                      if (value) {
                        appendContraindication({ value });
                        e.target.value = "";
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={(e) => {
                    const input = e.target
                      .closest("div")
                      .querySelector("input");
                    const value = input.value.trim();
                    if (value) {
                      appendContraindication({ value });
                      input.value = "";
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {contraindicationFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-1 bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm"
                  >
                    {field.value}
                    <button
                      type="button"
                      onClick={() => removeContraindication(index)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Side Effects */}
            <div className="space-y-4" id="side_effects">
              <Label>Side Effects</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add side effect..."
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const value = e.target.value.trim();
                      if (value) {
                        appendSideEffect({ value });
                        e.target.value = "";
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={(e) => {
                    const input = e.target
                      .closest("div")
                      .querySelector("input");
                    const value = input.value.trim();
                    if (value) {
                      appendSideEffect({ value });
                      input.value = "";
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {sideEffectFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm"
                  >
                    {field.value}
                    <button
                      type="button"
                      onClick={() => removeSideEffect(index)}
                      className="ml-2 text-yellow-600 hover:text-yellow-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Pre-Service Requirements */}
            <div className="space-y-4" id="pre_service_requirements">
              <Label>Pre-Service Requirements</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add pre-service requirement..."
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const value = e.target.value.trim();
                      if (value) {
                        appendPreReq({ value });
                        e.target.value = "";
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={(e) => {
                    const input = e.target
                      .closest("div")
                      .querySelector("input");
                    const value = input.value.trim();
                    if (value) {
                      appendPreReq({ value });
                      input.value = "";
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {preReqFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                  >
                    {field.value}
                    <button
                      type="button"
                      onClick={() => removePreReq(index)}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Post-Service Care */}
            <div className="space-y-4" id="post_service_care">
              <Label>Post-Service Care</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add post-service care instruction..."
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const value = e.target.value.trim();
                      if (value) {
                        appendPostCare({ value });
                        e.target.value = "";
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={(e) => {
                    const input = e.target
                      .closest("div")
                      .querySelector("input");
                    const value = input.value.trim();
                    if (value) {
                      appendPostCare({ value });
                      input.value = "";
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {postCareFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm"
                  >
                    {field.value}
                    <button
                      type="button"
                      onClick={() => removePostCare(index)}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    id="notes"
                    placeholder="Any additional notes about the service..."
                    rows={3}
                  />
                )}
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                disabled={isLoading}
                data-testid="service-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {isLoading ? "Saving..." : "Save Service"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
